import { Modal } from 'antd';
import { Row, Box, InputSearch, Colors, IcPlus } from 'basic-ui-lib';
import { Button } from 'basic-ui-lib/components/Button';
import { useState, forwardRef, useImperativeHandle, useRef, useMemo, useEffect, useCallback } from 'react';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import { ContentHeader, MediaTabContent } from './components';
import useGetManageLibrary from 'modules/media/services/hooks/useGetManageLibrary';
import { useGetMedia } from 'modules/channel/services/hooks/useGetMedia';
import AddWebviewLinkModal, { AddWebviewLinkModalRef } from '../AddWebviewLinkModal';

type Props = {
  onAddLink?: (item: IMediaItem) => void;
};

type Ref = {  
  show: (currentContent?: IMediaItem[], onConfirm?: (content: IMediaItem[]) => void) => void;
  hide: () => void;
};

export type ContentModalRef = Ref;

export const useContentModalRef = () => useRef<Ref>(null);

const ContentModal = forwardRef<Ref, Props>(({ onAddLink }, ref) => {
  const [libraryData, fetchLibrary] = useGetManageLibrary();
  const { items, pathStack, openFolder, onBack } = useGetMedia(libraryData, fetchLibrary);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('media');
  const [selection, setSelection] = useState<Map<string, IMediaItem>>(new Map());
  
  const onConfirmRef = useRef<(content: IMediaItem[]) => void>(() => undefined);
  const addWebviewLinkModalRef = useRef<AddWebviewLinkModalRef>(null);

  const currentFolder = pathStack[pathStack.length - 1] ?? { label: '', path: '' };
  
  useEffect(() => {
    if (!open) return;

    if (!libraryData) {
      fetchLibrary(' ');
    }
  }, [open]);

  const onShow: Ref['show'] = (currentContent, onConfirm) => {
    if (onConfirm) {
      onConfirmRef.current = onConfirm;
    }

    setOpen(true);
    const map = new Map<string, IMediaItem>();
    currentContent?.forEach((item) => {
      map.set(item.id, item);
    });
    
    setSelection(map);
    setSearchQuery('');
  };

  const onHide: Ref['hide'] = () => {
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    show: onShow,
    hide: onHide,
  }));

  const handleConfirm = () => {
    if (selection) {
      onConfirmRef.current(Array.from(selection.values()));
    }
    onHide();
  };

  const handleClose = () => {
    onHide();
  };

  const handleMediaItemClick = useCallback(
    (item: IMediaItem) => {
      if (item.type === MediaType.FOLDER) {
        openFolder(item);
        return;
      }

      setSelection((prev) => {
        const next = new Map(prev);

        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.set(item.id, item);
        }

        return next;
      });
    },
    [openFolder],
  );
  const handleBackClick = () => {
    onBack();
  };

  const selectedMediaIds = useMemo(() => {
    return Array.from(selection.keys());
  }, [selection]);

  const handleAddWebviewLink = () => {
    addWebviewLinkModalRef.current?.open((data) => {
      const newLink: IMediaItem = {
        id: '',
        name: data.name,
        url: data.url,
        type: MediaType.WEBVIEW,
        autoReplay: true,
        path: '',
        size: null,
      };

      onAddLink?.(newLink);
      onHide();
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      width={1048}
      centered
      destroyOnClose
      styles={{
        content: {
          padding: 0,
          borderRadius: 8,
        },
      }}
    >
      <Box display="flex" flexDirection="column" maxHeight="90vh">
        <ContentHeader onClose={handleClose} />

        <Box padding="16px 24px">
          <InputSearch
            placeholder="Search folder/image/video"
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </Box>
        <Box width="100%" padding="0 24px 12px" display="flex" justifyContent="flex-end">
          <Button lg type_new prefix={<IcPlus width={20} height={20} fill={Colors.primary[5]} />} onClick={handleAddWebviewLink}>
            Add Link
          </Button>
        </Box>
        <Box height={400} overflow="auto" padding="0 24px">
            <MediaTabContent
              items={items}
              currentPath={currentFolder.path}
              folderName={currentFolder.label}
              selectedMediaIds={selectedMediaIds}
              onMediaItemClick={handleMediaItemClick}
              onBackClick={handleBackClick}
            />
        </Box>

        <Row justifyContent="center" padding="16px 24px 24px">
          <Button 
            type_second 
            lg 
            onClick={handleConfirm}
            disabled={selection.size === 0}
            style={{ minWidth: 240 }}
          >
            OK
          </Button>
        </Row>
      </Box>
      <AddWebviewLinkModal ref={addWebviewLinkModalRef} />
    </Modal>
  );
});

ContentModal.displayName = 'ContentModal';

export default ContentModal;
